import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";

// Column definitions: [key, label, group]
// For pct columns: key ends with _pct, tooltip shows the count column (key without _pct)
const SETUP_COLS = [
  { key: "hood_id", label: "ID" },
  { key: "model_active", label: "Active", type: "bool" },
  { key: "has_boundary", label: "Boundary", type: "bool" },
  { key: "has_sqft_rules", label: "Sqft Rules", type: "bool" },
  { key: "has_ml_model", label: "ML Model", type: "bool" },
];

const BUILDING_COLS = [
  { key: "total_buildings", label: "Total", type: "num" },
  { key: "bldg_has_tier_pct", label: "Tier", countKey: "bldg_has_tier" },
  { key: "bldg_verified_pct", label: "Verified", countKey: "bldg_verified" },
  { key: "bldg_olr_mapped_pct", label: "OLR", countKey: "bldg_olr_mapped" },
  { key: "bldg_has_year_built_pct", label: "Year", countKey: "bldg_has_year_built" },
];

const SALES_COLS = [
  { key: "s_active", label: "Active", type: "num" },
  { key: "s_verified_pct", label: "Verified", countKey: "s_verified" },
  { key: "s_se_scraped_pct", label: "SE", countKey: "s_se_scraped" },
  { key: "s_ai_analyzed_pct", label: "AI", countKey: "s_ai_analyzed" },
  { key: "s_has_bbl_pct", label: "BBL", countKey: "s_has_bbl" },
  { key: "s_has_tax_pct", label: "Tax", countKey: "s_has_tax" },
  { key: "s_real_sqft_pct", label: "Real Sqft", countKey: "s_real_sqft" },
  { key: "s_has_dw_pct", label: "DW", countKey: "s_has_dw" },
  { key: "s_has_wd_pct", label: "W/D", countKey: "s_has_wd" },
  { key: "s_has_history_pct", label: "History", countKey: "s_has_history" },
];

const RENTAL_COLS = [
  { key: "r_active", label: "Active", type: "num" },
  { key: "r_verified_pct", label: "Verified", countKey: "r_verified" },
  { key: "r_se_scraped_pct", label: "SE", countKey: "r_se_scraped" },
  { key: "r_olr_scraped_pct", label: "OLR", countKey: "r_olr_scraped" },
  { key: "r_ai_analyzed_pct", label: "AI", countKey: "r_ai_analyzed" },
  { key: "r_real_sqft_pct", label: "Real Sqft", countKey: "r_real_sqft" },
  { key: "r_any_sqft_pct", label: "Any Sqft", countKey: "r_any_sqft" },
  { key: "r_has_dw_pct", label: "DW", countKey: "r_has_dw" },
  { key: "r_has_wd_pct", label: "W/D", countKey: "r_has_wd" },
  { key: "r_has_reno_pct", label: "Reno", countKey: "r_has_reno" },
  { key: "r_has_floor_pct", label: "Floor", countKey: "r_has_floor" },
  { key: "r_has_views_pct", label: "Views", countKey: "r_has_views" },
  { key: "r_has_dom_pct", label: "DOM", countKey: "r_has_dom" },
];

const CLOSED_RENTAL_COLS = [
  { key: "rc_total", label: "Total", type: "num" },
  { key: "rc_verified_pct", label: "Verified", countKey: "rc_verified" },
  { key: "rc_se_scraped_pct", label: "SE", countKey: "rc_se_scraped" },
  { key: "rc_olr_scraped_pct", label: "OLR", countKey: "rc_olr_scraped" },
  { key: "rc_ai_analyzed_pct", label: "AI", countKey: "rc_ai_analyzed" },
  { key: "rc_real_sqft_pct", label: "Real Sqft", countKey: "rc_real_sqft" },
  { key: "rc_any_sqft_pct", label: "Any Sqft", countKey: "rc_any_sqft" },
  { key: "rc_has_dw_pct", label: "DW", countKey: "rc_has_dw" },
  { key: "rc_has_wd_pct", label: "W/D", countKey: "rc_has_wd" },
  { key: "rc_has_reno_pct", label: "Reno", countKey: "rc_has_reno" },
  { key: "rc_has_floor_pct", label: "Floor", countKey: "rc_has_floor" },
  { key: "rc_has_views_pct", label: "Views", countKey: "rc_has_views" },
  { key: "rc_has_dom_pct", label: "DOM", countKey: "rc_has_dom" },
  { key: "rc_has_history_pct", label: "History", countKey: "rc_has_history" },
];

const COLUMN_GROUPS = [
  { label: "Setup", cols: SETUP_COLS },
  { label: "Buildings", cols: BUILDING_COLS },
  { label: "Active Sales", cols: SALES_COLS },
  { label: "Active Rentals", cols: RENTAL_COLS },
  { label: "Closed Rentals 2yr", cols: CLOSED_RENTAL_COLS },
];

const GROUP_COLORS = {
  Setup: "#f5f5f5",
  Buildings: "#e3f2fd",
  "Active Sales": "#fff3e0",
  "Active Rentals": "#e8f5e9",
  "Closed Rentals 2yr": "#fce4ec",
};

function formatPct(val) {
  if (val == null) return "-";
  return (val * 100).toFixed(1) + "%";
}

function formatBool(val) {
  return val ? "Yes" : "No";
}

function cellBg(col, val) {
  if (col.key.endsWith("_pct") && val != null && val >= 0.9) {
    return "#c8e6c9";
  }
  return undefined;
}

export default function CoverageStatsPage() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fetch neighborhood list (lightweight — just names/ids)
  useEffect(() => {
    authFetch("/buildings/neighborhoods")
      .then((res) => {
        const hoods = (res.neighborhoods || []).sort((a, b) => a.name.localeCompare(b.name));
        setNeighborhoods(hoods);
      })
      .catch((err) => console.error("Error fetching neighborhoods:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = (ids) => {
    if (!ids || ids.length === 0) return;
    setLoading(true);
    authFetch(`/coverage-stats?neighborhood_ids=${ids.join(",")}`)
      .then((rows) => setData(rows))
      .catch((err) => console.error("Error fetching coverage stats:", err))
      .finally(() => setLoading(false));
  };

  const toggleHood = (id) => {
    setSelectedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      return next;
    });
  };

  const applyFilter = () => {
    setDropdownOpen(false);
    if (selectedIds.length > 0) fetchData(selectedIds);
  };

  const clearFilter = () => {
    setSelectedIds([]);
    setDropdownOpen(false);
    setData([]);
  };

  const renderCell = (row, col) => {
    const val = row[col.key];
    if (col.type === "bool") return formatBool(val);
    if (col.key.endsWith("_pct")) return formatPct(val);
    if (col.type === "num") return val != null ? val.toLocaleString() : "-";
    return val != null ? String(val) : "-";
  };

  const getTooltip = (row, col) => {
    if (col.countKey) {
      const count = row[col.countKey];
      return count != null ? `${count} items` : "";
    }
    return "";
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#fafafa" }}>
      <Header showLogout />

      <div style={{ padding: "20px 30px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "22px", color: "#333" }}>
            Coverage Stats
          </h2>

          {/* Neighborhood multi-select dropdown */}
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              style={{
                padding: "8px 14px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontSize: "13px",
                color: "#333",
                minWidth: "200px",
                textAlign: "left",
              }}
            >
              {selectedIds.length === 0
                ? "All Neighborhoods"
                : `${selectedIds.length} selected`}
              <span style={{ float: "right" }}>{dropdownOpen ? "\u25B2" : "\u25BC"}</span>
            </button>

            {dropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: "4px",
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  zIndex: 100,
                  maxHeight: "300px",
                  overflowY: "auto",
                  minWidth: "240px",
                  padding: "4px 0",
                }}
              >
                {neighborhoods.map((h) => (
                  <label
                    key={h.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 12px",
                      cursor: "pointer",
                      fontSize: "13px",
                      color: "#333",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(h.id)}
                      onChange={() => toggleHood(h.id)}
                    />
                    {h.name}
                  </label>
                ))}
                <div
                  style={{
                    borderTop: "1px solid #eee",
                    padding: "8px 12px",
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <button
                    onClick={applyFilter}
                    style={{
                      flex: 1,
                      padding: "6px",
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Apply
                  </button>
                  <button
                    onClick={clearFilter}
                    style={{
                      flex: 1,
                      padding: "6px",
                      backgroundColor: "#eee",
                      color: "#333",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {data.length > 0 && (
            <button
              onClick={() => fetchData(selectedIds)}
              disabled={loading}
              style={{
                padding: "6px 14px",
                fontSize: "12px",
                backgroundColor: "#fff",
                color: "#1976d2",
                border: "1px solid #1976d2",
                borderRadius: "4px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1,
              }}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          )}
          {loading && !data.length && (
            <span style={{ fontSize: "13px", color: "#999" }}>Loading...</span>
          )}
        </div>

        {/* Table */}
        {data.length === 0 && !loading && (
          <div style={{ padding: "40px", textAlign: "center", color: "#999", fontSize: "14px" }}>
            Select neighborhoods and click Apply to load stats.
          </div>
        )}
        <div
          style={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "calc(100vh - 180px)",
            border: "1px solid #ddd",
            borderRadius: "8px",
            backgroundColor: "#fff",
            position: "relative",
          }}
        >
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: "12px",
              whiteSpace: "nowrap",
              minWidth: "100%",
            }}
          >
            <thead>
              {/* Group header row */}
              <tr>
                {/* Sticky name column header spanning 2 rows */}
                <th
                  rowSpan={2}
                  style={{
                    position: "sticky",
                    left: 0,
                    top: 0,
                    zIndex: 4,
                    backgroundColor: "#f5f5f5",
                    borderBottom: "2px solid #ccc",
                    borderRight: "2px solid #ccc",
                    padding: "6px 10px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Neighborhood
                </th>
                {COLUMN_GROUPS.map((g) => (
                  <th
                    key={g.label}
                    colSpan={g.cols.length}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: GROUP_COLORS[g.label] || "#f5f5f5",
                      borderBottom: "1px solid #ddd",
                      borderRight: "2px solid #ccc",
                      padding: "6px 8px",
                      textAlign: "center",
                      fontWeight: 600,
                      fontSize: "13px",
                    }}
                  >
                    {g.label}
                  </th>
                ))}
              </tr>
              {/* Column header row */}
              <tr>
                {COLUMN_GROUPS.map((g) =>
                  g.cols.map((col, ci) => (
                    <th
                      key={col.key}
                      style={{
                        position: "sticky",
                        top: "29px",
                        zIndex: 2,
                        backgroundColor: GROUP_COLORS[g.label] || "#f5f5f5",
                        borderBottom: "2px solid #ccc",
                        borderRight:
                          ci === g.cols.length - 1
                            ? "2px solid #ccc"
                            : "1px solid #eee",
                        padding: "5px 7px",
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "11px",
                        color: "#555",
                      }}
                    >
                      {col.label}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((row, ri) => (
                <tr
                  key={row.hood_id}
                  style={{
                    backgroundColor: ri % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  {/* Sticky name column */}
                  <td
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: ri % 2 === 0 ? "#fff" : "#fafafa",
                      borderRight: "2px solid #ccc",
                      padding: "5px 10px",
                      fontWeight: 500,
                      color: "#333",
                    }}
                  >
                    {row.neighborhood_name}
                  </td>
                  {COLUMN_GROUPS.map((g) =>
                    g.cols.map((col, ci) => (
                      <td
                        key={col.key}
                        title={getTooltip(row, col)}
                        style={{
                          padding: "5px 7px",
                          textAlign: "center",
                          borderRight:
                            ci === g.cols.length - 1
                              ? "2px solid #ccc"
                              : "1px solid #f0f0f0",
                          backgroundColor:
                            cellBg(col, row[col.key]) ||
                            (ri % 2 === 0 ? "#fff" : "#fafafa"),
                          color:
                            col.type === "bool"
                              ? row[col.key]
                                ? "#2e7d32"
                                : "#c62828"
                              : "#333",
                        }}
                      >
                        {renderCell(row, col)}
                      </td>
                    ))
                  )}
                </tr>
              ))}
              {data.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={
                      1 +
                      COLUMN_GROUPS.reduce((s, g) => s + g.cols.length, 0)
                    }
                    style={{
                      padding: "30px",
                      textAlign: "center",
                      color: "#999",
                    }}
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
